'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var tslib = require('tslib');
var jsxRuntime = require('react/jsx-runtime');
var cx = require('classnames');
var PropTypes = require('prop-types');
var react = require('react');
var AlloyButton = require('../alloy-button/AlloyButton.js');
var CloseIcon = require('../alloy-icon/CloseIcon.js');
var reducers = require('./reducers.js');
var reducerTypes = require('./reducerTypes.js');
var toast_module = require('./toast.module.scss.js');
var ToastContext = require('./ToastContext.js');
var ToastTypes = require('./ToastTypes.js');

function _interopDefault (e) { return e && e.__esModule ? e : { default: e }; }

var cx__default = /*#__PURE__*/_interopDefault(cx);
var PropTypes__default = /*#__PURE__*/_interopDefault(PropTypes);

var ToastManager = function (_a) {
    var children = _a.children;
    var DEFAULT_TIMEOUT = 3500;
    var _b = react.useReducer(reducers.toastReducer, { toasts: [] }), toastState = _b[0], dispatch = _b[1];
    var destroyToast = react.useCallback(function (id) { return dispatch({ type: reducerTypes.ActionTypes.DESTROY_TOAST, id: id }); }, []);
    var destroyAllToasts = react.useCallback(function () { return dispatch({ type: reducerTypes.ActionTypes.DESTROY_ALL_TOASTS }); }, []);
    var createToast = react.useCallback(function (message, severity, currentTimeout, hideCloseButton) {
        if (severity === void 0) { severity = ToastTypes.SeverityType.SUCCESS; }
        var timeoutStart = Date.now();
        var timeoutId = null;
        var infinite = currentTimeout === Infinity;
        if (!infinite) {
            timeoutId = setTimeout(function () {
                destroyToast(timeoutStart);
            }, currentTimeout);
        }
        var displayTime = currentTimeout === DEFAULT_TIMEOUT
            ? ToastTypes.DisplayTimeType.DEFAULT
            : ToastTypes.DisplayTimeType.CUSTOM;
        dispatch({
            type: reducerTypes.ActionTypes.CREATE_TOAST,
            toast: {
                id: timeoutStart,
                timeoutId: timeoutId,
                infinite: infinite,
                timeoutRemaining: currentTimeout,
                message: message,
                severity: severity,
                hideCloseButton: hideCloseButton,
                displayTime: displayTime,
                timeoutStart: timeoutStart,
                timeoutLength: currentTimeout - 500
            }
        });
    }, [destroyToast]);
    var createSuccessToast = react.useCallback(function (message, ms, hideCloseButton) {
        if (hideCloseButton === void 0) { hideCloseButton = false; }
        var currentTimeout = ms ? ms + 500 : DEFAULT_TIMEOUT;
        createToast(message, ToastTypes.SeverityType.SUCCESS, currentTimeout, hideCloseButton);
    }, [createToast]);
    var createErrorToast = react.useCallback(function (message, ms, hideCloseButton) {
        if (hideCloseButton === void 0) { hideCloseButton = false; }
        var currentTimeout = ms ? ms + 500 : DEFAULT_TIMEOUT;
        createToast(message, ToastTypes.SeverityType.ERROR, currentTimeout, hideCloseButton);
    }, [createToast]);
    var createWarningToast = react.useCallback(function (message, ms, hideCloseButton) {
        if (hideCloseButton === void 0) { hideCloseButton = false; }
        var currentTimeout = ms ? ms + 500 : DEFAULT_TIMEOUT;
        createToast(message, ToastTypes.SeverityType.WARNING, currentTimeout, hideCloseButton);
    }, [createToast]);
    var updateToastTimeout = function (toast) {
        return dispatch({
            type: reducerTypes.ActionTypes.UPDATE_TOAST_TIMEOUT,
            toast: toast
        });
    };
    var pauseToastTimer = function (toastId) {
        var toast = toastState.toasts.find(function (toastNode) { return toastNode.id === toastId; });
        if (toast && toast.timeoutId) {
            clearTimeout(toast.timeoutId);
            var timeoutRemaining = toast.timeoutRemaining - (Date.now() - toast.timeoutStart);
            updateToastTimeout(tslib.__assign(tslib.__assign({}, toast), { timeoutRemaining: timeoutRemaining }));
        }
    };
    var resetToastTimer = function (toastId) {
        var toast = toastState.toasts.find(function (toastNode) { return toastNode.id === toastId; });
        if (toast) {
            var timeoutStart = Date.now();
            var timeoutRemaining = (toast === null || toast === void 0 ? void 0 : toast.timeoutRemaining) || DEFAULT_TIMEOUT;
            var timeoutId = setTimeout(function () {
                destroyToast(toastId);
            }, timeoutRemaining);
            updateToastTimeout(tslib.__assign(tslib.__assign({}, toast), { timeoutId: timeoutId, timeoutStart: timeoutStart }));
        }
    };
    var getToasts = function (toasts) {
        return toasts.map(function (toast) { return (jsxRuntime.jsxs("div", tslib.__assign({ className: cx__default.default(toast_module.default['toast__message'], 'position-relative', toast_module.default[toast.severity], toast.displayTime, toast.infinite
                ? toast_module.default['fadein-only']
                : toast_module.default['fadein-fadeout'], { 'ps-3': toast.hideCloseButton }), style: {
                animationDelay: '0s, ' + toast.timeoutLength + 'ms',
                WebkitAnimationDelay: '0s, ' + toast.timeoutLength + 'ms'
            }, onMouseEnter: function () {
                return !toast.infinite && pauseToastTimer(toast.id);
            }, onMouseLeave: function () {
                return !toast.infinite && resetToastTimer(toast.id);
            } }, { children: [jsxRuntime.jsx("div", tslib.__assign({ className: toast_module.default['toast__dismisser'] }, { children: !toast.hideCloseButton && (jsxRuntime.jsx(AlloyButton.AlloyButton, tslib.__assign({ buttonType: "ghost", onClick: function () { return destroyToast(toast.id); }, className: "ps-0 py-0" }, { children: jsxRuntime.jsx(CloseIcon.default, {}) }))) })), typeof toast.message === 'string' ? (toast.message) : (jsxRuntime.jsx("div", tslib.__assign({ className: "".concat(toast_module.default['toast__message-container']), "data-testid": "toast__message-container" }, { children: toast.message })))] }), toast.id)); });
    };
    var context = react.useMemo(function () { return ({
        toasts: toastState.toasts,
        createSuccessToast: createSuccessToast,
        createErrorToast: createErrorToast,
        createWarningToast: createWarningToast,
        destroyToast: destroyToast,
        destroyAllToasts: destroyAllToasts
    }); }, [
        createErrorToast,
        createSuccessToast,
        createWarningToast,
        destroyToast,
        destroyAllToasts,
        toastState.toasts
    ]);
    return (jsxRuntime.jsxs(ToastContext.default.Provider, tslib.__assign({ value: context }, { children: [children, jsxRuntime.jsx("div", tslib.__assign({ className: "".concat(toast_module.default['toast__container']) }, { children: getToasts(toastState.toasts) }))] })));
};
ToastManager.propTypes = {
    children: PropTypes__default.default.node.isRequired
};
var ToastManager$1 = ToastManager;

exports.default = ToastManager$1;
//# sourceMappingURL=ToastManager.js.map
